/**
 * Text input for keyword entry during a SIM session.
 *
 * @module components/sim/takeSim/KeywordInput
 *
 * @author Snowfly
 * @version 1.0.0
 */
/* eslint-disable react/no-danger */
import {Input} from '@material-ui/core';
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Component that renders an input field for capturing keyword-based input,
 * with conditional rendering for links and formatting for phone numbers.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.classes - Object containing CSS class names for styling.
 * @param {Function} props.handleBlur - Function to handle the blur event on the input field.
 * @param {Function} props.handleChange - Function to handle the change event on the input field.
 * @param {Object} props.fieldName - Object representing the field's metadata and current state.
 * @param {string} props.fieldName.value - The current value of the field.
 * @param {string} props.fieldName.fieldTitle - The title or name of the field.
 * @param {string} props.fieldName.type - The type of the input field (e.g., text, number).
 * @param {boolean} props.fieldName.is_scorable - Boolean flag indicating whether the field is scorable.
 * @param {string} props.fieldName._id - Unique identifier for the field.
 * @param {string} [props.fieldName.current_value] - The current value for conditional formatting.
 * @param {Function} [props.fetchCaseCallerDetails] - Optional function to fetch additional details when specific keys are pressed.
 *
 * @returns {JSX.Element} JSX markup containing either a formatted link or an input field.
 *
 * @remarks
 * - If the field value starts with an anchor (`<a>`) tag, it displays the value as a styled link.
 * - If the field's current value starts with a digit, it is formatted as a phone number.
 * - Includes data attributes for metadata related to the field.
 */
const KeywordInput = ({
    classes,
    handleBlur,
    handleChange,
    fieldName,
    fetchCaseCallerDetails,
}) => {
    const numberList = '0123456789';

    /**
     * Modifies a given string by inserting an attribute at a specified position.
     *
     * This function is primarily used to format URLs by embedding the attribute
     * `rel="noopener noreferrer"` into the string at a predefined position.
     *
     * @param {string} str - The input string to be formatted.
     * @returns {string} The resulting string after embedding the attribute.
     */
    const formatUrl = (str) => {
        const attribute = ' rel="noopener noreferrer"';
        const position = 2;
        const output = [
            str.slice(0, position),
            attribute,
            str.slice(position),
        ].join('');
        return output;
    };

    /**
     * Formats a given phone number string into a standardized format.
     *
     * This function removes any non-numeric characters from the input string
     * and formats it in the style "(XXX) XXX-XXXX". If the input includes a leading
     * "1" as a country code, the formatted number will include "+1 " at the beginning.
     *
     * If the input does not match the expected structure of a phone number, the function
     * will return the original input string unmodified.
     *
     * @param {string} phoneNumberString - The phone number string to be formatted.
     * @returns {string} The formatted phone number or the original string if formatting is not possible.
     */
    const formatPhoneNumber = (phoneNumberString) => {
        const cleaned = (`${phoneNumberString}`).replace(/\D/g, '');
        const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            const intlCode = match[1] ? '+1 ' : '';
            return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
        }
        return phoneNumberString;
    };

    return (
        <div>
            {fieldName && fieldName.value && fieldName.value.startsWith('<a ') ? (
                <div className={classes.linkTag}>
          <span
              className={classes.anim}
              dangerouslySetInnerHTML={{
                  __html: formatUrl(fieldName.value),
              }}
          />
                </div>
            ) : (
                <Input
                    fullWidth
                    onKeyUp={
                        fieldName.fieldTitle === 'Caller Name'
                            ? fetchCaseCallerDetails
                            : null
                    }
                    value={
                        fieldName.current_value
                            ? numberList.includes(fieldName.current_value[0])
                                ? formatPhoneNumber(fieldName.current_value)
                                : fieldName.current_value
                            : ''
                    }
                    onBlur={handleBlur}
                    onAnimationEnd={handleBlur}
                    inputProps={{
                        'data-currentvalue': fieldName.value,
                        'data-caseid': fieldName._id,
                        'data-inputtype': fieldName.type,
                        'data-isscorable': fieldName.is_scorable,
                        'data-fieldtitle': fieldName.fieldTitle,
                    }}
                    name={fieldName.fieldTitle}
                    onChange={handleChange}
                    className={fieldName.current_value ? classes.anim : null}
                    // endAdornment={(
                    //   <InputAdornment position="end">
                    //     <EditIcon className={classes.editIcon} />
                    //   </InputAdornment>
                    // )}
                />
            )}
        </div>
    );
};

/**
 * PropTypes definition for the KeywordInput component.
 *
 * This object specifies the type and constraints for the props
 * that the KeywordInput component can accept.
 *
 * Properties:
 * - onChange: A required function that is triggered when the input value changes.
 * - value: A string representing the current value of the input field.
 * - placeholder: An optional string to display as a placeholder in the input field.
 * - maxLength: An optional number that indicates the maximum length of the input.
 * - disabled: A boolean indicating whether the input field should be disabled.
 */
KeywordInput.propTypes = {
    classes: PropTypes.object,
    handleBlur: PropTypes.func,
    handleChange: PropTypes.func,
    fieldName: PropTypes.object,
    fetchCaseCallerDetails: PropTypes.func,
};

export default KeywordInput;
